import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';

import * as CannedMessagesActions from './canned-messages.actions';
import { CannedMessage } from '@care-portals/api-interfaces';

export const CANNED_MESSAGES_FEATURE_KEY = 'cannedMessages';

interface CannedMessageEntity extends CannedMessage {}

export interface State extends EntityState<CannedMessageEntity> {
  selectedId?: string;
  loading: boolean;
  error?: string | null;
}

export interface CannedMessagesPartialState {
  readonly [CANNED_MESSAGES_FEATURE_KEY]: State;
}

export function selectCannedMessageId(cannedMessage: CannedMessage): string {
  return cannedMessage._id;
}

export const CannedMessagesAdapter: EntityAdapter<CannedMessageEntity> =
  createEntityAdapter<CannedMessageEntity>({
    selectId: selectCannedMessageId
  });

export const initialState: State = CannedMessagesAdapter.getInitialState({
  selectedId: null,
  loading: false,
  error: null
});

const cannedMessagesReducer = createReducer(
  initialState,
  on(CannedMessagesActions.getCannedMessages, state => ({
    ...state,
    loading: true
  })),
  on(
    CannedMessagesActions.getCannedMessagesSuccess,
    (state, { cannedMessages }) =>
      CannedMessagesAdapter.setAll(cannedMessages, {
        ...state,
        loading: false
      })
  ),
  on(CannedMessagesActions.getCannedMessagesFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false
  })),
  on(CannedMessagesActions.addCannedMessage, state => ({
    ...state,
    loading: true,
    error: null
  })),
  on(
    CannedMessagesActions.addCannedMessageSuccess,
    (state, { cannedMessage }) =>
      CannedMessagesAdapter.addOne(cannedMessage, {
        ...state,
        loading: false
      })
  ),
  on(CannedMessagesActions.addCannedMessageFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false
  })),
  on(CannedMessagesActions.updateCannedMessage, state => ({
    ...state,
    loading: true,
    error: null
  })),
  on(
    CannedMessagesActions.updateCannedMessageSuccess,
    (state, { cannedMessage }) =>
      CannedMessagesAdapter.updateOne(
        { id: cannedMessage._id, changes: cannedMessage },
        {
          ...state,
          loading: false
        }
      )
  ),
  on(CannedMessagesActions.updateCannedMessageFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false
  })),
  on(CannedMessagesActions.deleteCannedMessage, state => ({
    ...state,
    loading: true,
    error: null
  })),
  on(CannedMessagesActions.deleteCannedMessageSuccess, (state, { id }) =>
    CannedMessagesAdapter.removeOne(id, {
      ...state,
      loading: false
    })
  ),
  on(CannedMessagesActions.deleteCannedMessageFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false
  })),
  on(CannedMessagesActions.selectCannedMessage, (state, { id }) => ({
    ...state,
    selectedId: id
  }))
);

export function reducer(state: State | undefined, action: Action) {
  return cannedMessagesReducer(state, action);
}
